<template>
  <div class="dashboard-wrapper" :aria-busy="loading ? 'true' : 'false'">
    <div v-if="loading && !showNoDashboardMsg" class="loading-container" aria-hidden="true">
      <div class="loading-content">
        <div class="loading-logo">
          <div class="loading-icon-wrapper">
            <v-icon size="32" color="#3b82f6">mdi-view-dashboard</v-icon>
          </div>
          <div class="loading-pulse"></div>
        </div>
        <div class="loading-text">Loading Dashboard</div>
        <div class="loading-subtext">Fetching your analytics data...</div>
        <div class="loading-progress">
          <div class="loading-progress-bar"></div>
        </div>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    </div>

    <div v-else-if="!showNoDashboardMsg" class="dashboard-content">
      <!-- Modern Hero Section -->
      <div class="dashboard-hero">
        <div class="hero-left">
          <div class="hero-icon">
            <v-icon size="26" color="#3b82f6">mdi-view-dashboard</v-icon>
          </div>
          <div class="hero-text">
            <h1 class="dashboard-title">Dashboard Overview</h1>
            <p class="dashboard-subtitle">Key CPD and trainee insights at a glance</p>
          </div>
        </div>
        <div class="hero-right">
          <div class="hero-date">
            <v-icon size="16" class="mr-2" color="#64748b">mdi-calendar-today</v-icon>
            {{ currentDate }}
          </div>
          <v-btn class="hero-help-btn" depressed small @click="openHelpFaq">
            <v-icon left size="16">mdi-lifebuoy</v-icon>
            Help
          </v-btn>
        </div>
      </div>
      <div v-if="readOnlyNotice" class="renewal-alert">
        <v-icon size="20" class="renewal-alert__icon">mdi-alert-circle</v-icon>
        <div class="renewal-alert__content">
          <div class="renewal-alert__title">License expired</div>
          <div class="renewal-alert__text">
            <span v-if="readOnlyNotice.date">Expired on {{ readOnlyNotice.date }}.</span>
            Please renew license to use the functionality.
          </div>
        </div>
      </div>
      <!-- Modern Filter Section -->
      <div class="filter-section">
        <div class="filter-header">
          <v-icon size="20" class="filter-icon">mdi-filter-variant</v-icon>
          <span class="filter-title">Filters</span>
        </div>
        <div class="filter-grid">
          <div v-if="isMohUser || isRhbUser" class="filter-item">
            <v-autocomplete
                v-model="regionValue"
                :clearable="clearable"
                :items="REGION"
                :loading="regionLoading"
                item-text="name"
                item-value="id"
                label="Region"
                return-object
                outlined
                dense
                hide-details
                class="modern-input"
            ></v-autocomplete>
          </div>
          <div v-if="isMohUser || isRhbUser" class="filter-item">
            <v-autocomplete
                v-model="accreditorValue"
                :clearable="clearable"
                :items="ACCREDTOR"
                :loading="accreditorLoading"
                @focus="ensureAccreditorsLoaded"
                item-text="accreditorName"
                item-value="id"
                label="Accreditor"
                return-object
                outlined
                dense
                hide-details
                class="modern-input"
            ></v-autocomplete>
          </div>
          <div v-if="isMohUser || isRhbUser || isAccreditorUser" class="filter-item">
            <v-autocomplete
                v-model="cpdProviderValue"
                :clearable="clearable"
                :items="CPD_PROVIDERS"
                :loading="cpdLoading"
                @focus="ensureCPDProvidersLoaded"
                item-text="trainingCenterName"
                item-value="trainingCenterName"
                label="CPD Provider"
                return-object
                outlined
                dense
                hide-details
                class="modern-input"
            ></v-autocomplete>
          </div>
          <div class="filter-item">
            <v-menu ref="menu" v-model="startMenu" :close-on-content-click="false" max-width="290px"
                    min-width="290px" offset-y transition="scale-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="startDateValue"
                    :clearable="true"
                    placeholder="Start Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                ></v-text-field>
              </template>
              <v-date-picker ref="startDateValue" v-model="startDateValue" no-title
                             placeholder="Start Date" scrollable @input="startMenu = false"></v-date-picker>
            </v-menu>
          </div>
          <div class="filter-item">
            <v-menu ref="menu" v-model="startMenu2" :close-on-content-click="false" max-width="290px"
                    min-width="290px" offset-y transition="scale-transition">
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model="endDateValue"
                    :clearable="true"
                    placeholder="End Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                ></v-text-field>
              </template>
              <v-date-picker ref="endDateValue" v-model="endDateValue" no-title
                             placeholder="End Date" scrollable @input="startMenu2 = false"></v-date-picker>
            </v-menu>
          </div>
        </div>
      </div>
      <!-- KPI Cards Section -->
      <div v-if="isMohUser ||isRhbUser || isAccreditorUser || isCpdProviderUser" class="kpi-section">
        <div class="section-header">
          <v-icon size="20" class="section-icon">mdi-chart-box-outline</v-icon>
          <span class="section-title">Key Metrics</span>
        </div>
        <v-row class="kpi-grid">
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser || isCpdProviderUser) && !accreditorValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-account-group</v-icon>
                  </div>
                  <div class="kpi-label">Total No. of Health Professionals</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeInfo?.total?.hraTotal + traineeInfo?.total?.newTotal"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser || isCpdProviderUser) && !accreditorValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-clipboard-text</v-icon>
                  </div>
                  <div class="kpi-label">Trainees Under CPD Activity</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeInfo?.notCompleted?.hraNotCompleted + traineeInfo?.notCompleted?.newNotCompleted"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser || isCpdProviderUser) && !accreditorValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-check-circle</v-icon>
                  </div>
                  <div class="kpi-label">Total Trainees who took CEU</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeInfo?.passed?.hraPass + traineeInfo?.passed?.newPass"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser || isCpdProviderUser) && !accreditorValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-alert-circle</v-icon>
                  </div>
                  <div class="kpi-label">Total Trainees who failed</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeInfo?.failed?.hraFail + traineeInfo?.failed?.newFail"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser) && !accreditorValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-book-open-variant</v-icon>
                  </div>
                  <div class="kpi-label">Total Courses taken by trainees</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeSummary.TotalCEUTookByTrainee"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col
              v-if="(isMohUser ||isRhbUser || isAccreditorUser) && !cpdProviderValue"
              cols="12"
              md="2" class="kpi-col"
          >
            <v-card class="kpi-card" outlined shaped to="courseReport">
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-timer-sand</v-icon>
                  </div>
                  <div class="kpi-label">Courses registered (past 3 months) not yet accredited</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="traineeSummary.nonAccreditedCourse"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser || isAccreditorUser) && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-school</v-icon>
                  </div>
                  <div class="kpi-label">CPD providers who accredited ≥1 course</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="accreditorSummary?.['no_course_cpd']"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser) && !accreditorValue && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-seal-variant</v-icon>
                  </div>
                  <div class="kpi-label">Accreditors who accredited ≥1 course</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="accreditorSummary?.['no_course_acc']"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser) && !accreditorValue && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped>
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-account-check</v-icon>
                  </div>
                  <div class="kpi-label">Accreditors who accredited ≥1 CPD provider</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="accreditorSummary?.['no_cpd']"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser || isAccreditorUser) && !accreditorValue && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped to="renewalStatusReport">
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-calendar-plus</v-icon>
                  </div>
                  <div class="kpi-label">CPD providers registered in last 3 months</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="upcomingRenewals?.newlyCpdProvider"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser) && !accreditorValue && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped to="renewalStatusReport">
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-calendar-plus-outline</v-icon>
                  </div>
                  <div class="kpi-label">Accreditors registered in last 3 months</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="upcomingRenewals?.newlyAccreditor"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser || isAccreditorUser) && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped to="renewalStatusReport">
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-history</v-icon>
                  </div>
                  <div class="kpi-label">CPD providers needing renewal in 2 months</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="upcomingRenewals?.cpdProvider"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col v-if="(isMohUser ||isRhbUser) && !accreditorValue && !cpdProviderValue" cols="12" md="2" class="kpi-col">
            <v-card class="kpi-card" outlined shaped to="renewalStatusReport">
              <v-card-text class="kpi-content">
                <div class="kpi-header">
                  <div class="kpi-icon">
                    <v-icon>mdi-update</v-icon>
                  </div>
                  <div class="kpi-label">Accreditors needing renewal in 2 months</div>
                </div>
                <div class="kpi-value">
                  <ICountUp
                      :delay="delay"
                      :endVal="upcomingRenewals?.accreditor"
                      :options="options"
                      :startVal="count"
                  />
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </div>

      <!-- Trainer Dashboard Section -->
      <div class="trainer-section">
        <div class="section-header">
          <v-icon size="20" class="section-icon">mdi-account-tie-outline</v-icon>
          <span class="section-title">Trainer Dashboard</span>
        </div>
        <div class="trainer-grid">
          <div class="trainer-panel">
            <div class="trainer-panel__header">
              <div class="trainer-panel__title">Trainer Filters</div>
              <div class="trainer-panel__subtitle">Filter totals by selection criteria, type, and profession.</div>
            </div>
            <div class="filter-grid">
              <div class="filter-item">
                <v-autocomplete
                    v-model="trainerSelectionCriteriaValue"
                    :clearable="clearable"
                    :items="TRAINER_SELECTION_CRITERIAS"
                    :loading="trainerCriteriaLoading"
                    @focus="ensureTrainerLookupsLoaded"
                    item-text="name"
                    item-value="id"
                    label="Trainer Selection Criteria"
                    return-object
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                ></v-autocomplete>
              </div>
              <div class="filter-item">
                <v-autocomplete
                    v-model="trainerTypeValue"
                    :clearable="clearable"
                    :items="TRAINER_TYPES"
                    :loading="trainerTypesLoading"
                    @focus="ensureTrainerLookupsLoaded"
                    item-text="name"
                    item-value="id"
                    label="Trainer Type"
                    return-object
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                ></v-autocomplete>
              </div>
              <div class="filter-item">
                <v-autocomplete
                    v-model="trainerProfessionValue"
                    :clearable="clearable"
                    :items="TRAINER_PROFESSIONS"
                    :loading="trainerProfessionLoading"
                    @focus="ensureTrainerLookupsLoaded"
                    item-text="name"
                    item-value="id"
                    label="Profession"
                    return-object
                    outlined
                    dense
                    hide-details
                    class="modern-input"
                ></v-autocomplete>
              </div>
            </div>
          </div>
          <div class="trainer-panel trainer-panel--summary">
            <div class="trainer-summary">
              <div class="trainer-summary__label">Total Trainers</div>
              <div class="trainer-summary__value">
                <v-progress-circular
                    v-if="trainerCountLoading"
                    indeterminate
                    color="#3b82f6"
                    size="32"
                    width="3"
                ></v-progress-circular>
                <ICountUp
                    v-else
                    :delay="delay"
                    :endVal="trainerCount"
                    :options="options"
                    :startVal="count"
                />
              </div>
              <div class="trainer-summary__meta">Matches current filters</div>
              <div class="trainer-summary__details">
                <div class="trainer-summary__item">
                  <span class="trainer-summary__item-label">Selection Criteria</span>
                  <span class="trainer-summary__item-value">{{ trainerSelectionCriteriaValue?.name || "All" }}</span>
                </div>
                <div class="trainer-summary__item">
                  <span class="trainer-summary__item-label">Trainer Type</span>
                  <span class="trainer-summary__item-value">{{ trainerTypeValue?.name || "All" }}</span>
                </div>
                <div class="trainer-summary__item">
                  <span class="trainer-summary__item-label">Profession</span>
                  <span class="trainer-summary__item-value">{{ trainerProfessionValue?.name || "All" }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Section -->
      <div class="charts-section">
        <div class="section-header">
          <v-icon size="20" class="section-icon">mdi-chart-line</v-icon>
          <span class="section-title">Analytics & Trends</span>
        </div>

        <!-- Main Trend Chart -->
        <div class="chart-card chart-card--full">
          <div class="chart-header">
            <div class="chart-header-left">
              <div class="chart-icon">
                <v-icon size="18" color="#64748b">mdi-trending-up</v-icon>
              </div>
              <div>
                <div class="chart-title">Trainees Trend</div>
                <div class="chart-subtitle">Rolling completions across dates</div>
              </div>
            </div>
          </div>
          <div class="chart-body">
            <apexchart
                v-if="loaded"
                :options="traineeOption"
                :series="traineeSeries"
                height="350"
                type="area"
            ></apexchart>
          </div>
        </div>

        <!-- Charts Grid -->
        <v-row class="charts-grid">
          <v-col cols="12" md="4">
            <div class="chart-card chart-card--fixed">
              <div class="chart-header">
                <div class="chart-header-left">
                  <div class="chart-icon">
                    <v-icon size="18" color="#64748b">mdi-chart-donut</v-icon>
                  </div>
                  <div>
                    <div class="chart-title">CPD Activity</div>
                    <div class="chart-subtitle">Total {{ totalActivity }} across categories</div>
                  </div>
                </div>
              </div>
              <div class="chart-body chart-body--centered">
                <apexchart
                    v-if="loaded"
                    :options="cpdActivityOptions"
                    :series="cpdActivitySeries"
                    height="280"
                    type="donut"
                ></apexchart>
              </div>
            </div>
          </v-col>
          <v-col v-if="isMohUser || isRhbUser" cols="12" md="4">
            <div class="chart-card chart-card--fixed">
              <div class="chart-header">
                <div class="chart-header-left">
                  <div class="chart-icon">
                    <v-icon size="18" color="#64748b">mdi-chart-bar</v-icon>
                  </div>
                  <div>
                    <div class="chart-title">Accreditors by Ownership</div>
                    <div class="chart-subtitle">Total {{ totalAccreditor }} accreditors</div>
                  </div>
                </div>
              </div>
              <div class="chart-body">
                <apexchart
                    v-if="loaded"
                    :options="accreditorOptions"
                    :series="accreditorSeries"
                    height="280"
                    type="bar"
                ></apexchart>
              </div>
            </div>
          </v-col>
          <v-col v-if="isMohUser || isRhbUser || isAccreditorUser" cols="12" md="4">
            <div class="chart-card chart-card--fixed">
              <div class="chart-header">
                <div class="chart-header-left">
                  <div class="chart-icon">
                    <v-icon size="18" color="#64748b">mdi-chart-bar</v-icon>
                  </div>
                  <div>
                    <div class="chart-title">CPD Providers by Ownership</div>
                    <div class="chart-subtitle">Total {{ totalCpd }} providers</div>
                  </div>
                </div>
              </div>
              <div class="chart-body">
                <apexchart
                    v-if="loaded"
                    :options="cpdProviderOptions"
                    :series="cpdProviderSeries"
                    height="280"
                    type="bar"
                ></apexchart>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </div>

    <!-- No Dashboard Message -->
    <div v-else class="no-data-container">
      <div class="no-data-content">
        <div class="no-data-icon">
          <v-icon size="64" color="#94a3b8">mdi-chart-box-outline</v-icon>
        </div>
        <h2 class="no-data-title">No Dashboard Available</h2>
        <p class="no-data-subtitle">There's no dashboard data to display for your account.</p>
      </div>
    </div>

    <v-dialog v-model="showHelpFaq" max-width="960" scrollable>
      <v-card class="dashboard-help-dialog">
        <v-card-title class="dashboard-help-dialog__title">
          <div class="dashboard-help-dialog__title-wrap">
            <div class="dashboard-help-dialog__eyebrow">Support Center</div>
            <div>Help & FAQ</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeHelpFaq">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="dashboard-help-dialog__body">
          <div class="dashboard-help-dialog__intro mb-4">
            Quick answers to common questions for CPD providers and HRD users.
          </div>

          <div class="dashboard-help-toolbar">
            <v-text-field
                v-model="helpFaqSearch"
                dense
                outlined
                clearable
                hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Search a question or keyword"
                class="dashboard-help-search"
            ></v-text-field>
            <div class="dashboard-help-categories">
              <button
                  v-for="category in helpFaqCategories"
                  :key="category"
                  type="button"
                  class="dashboard-help-chip"
                  :class="{'dashboard-help-chip--active': helpFaqCategory === category}"
                  @click="helpFaqCategory = category"
              >
                {{ category }}
              </button>
            </div>
          </div>

          <div class="dashboard-help-results-count">
            Showing {{ filteredHelpFaqs.length }} question<span v-if="filteredHelpFaqs.length !== 1">s</span>
          </div>

          <v-expansion-panels v-if="filteredHelpFaqs.length" accordion popout>
            <v-expansion-panel v-for="(faq, index) in filteredHelpFaqs" :key="faq.question">
              <v-expansion-panel-header class="dashboard-help-panel-header">
                <div class="dashboard-help-panel__head">
                  <span class="dashboard-help-panel__index">{{ index + 1 }}</span>
                  <div class="dashboard-help-panel__question">{{ faq.question }}</div>
                </div>
                <div class="dashboard-help-panel__category">{{ faq.category }}</div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <p v-if="faq.answer" class="dashboard-help-dialog__answer">{{ faq.answer }}</p>
                <ul v-if="faq.steps" class="dashboard-help-dialog__steps">
                  <li v-for="step in faq.steps" :key="step">{{ step }}</li>
                </ul>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

          <div v-else class="dashboard-help-empty">
            <v-icon size="28" color="#94a3b8">mdi-text-box-search-outline</v-icon>
            <div class="dashboard-help-empty__title">No matching FAQ found</div>
            <div class="dashboard-help-empty__subtitle">Try another keyword or clear category filters.</div>
            <v-btn text small color="primary" @click="resetHelpFaqFilters">Reset filters</v-btn>
          </div>

          <div class="dashboard-help-footer">
            Need account support? Contact the MOH iHRIS administrator.
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {DashboardService} from "./dashboard.service";
import moment from "moment";
import ICountUp from "vue-countup-v2";
import {
  getHealthProfessions,
  getRegionList,
  getTrainerSelectionCriteriaList,
  getTrainerTypes,
  getUser,
} from "../../utils/helper";
import {AccreditorService} from "@/components/Accreditors/accreditor.service";
import {TrainingCenterService} from "@/components/TrainingCenter/training_center.service";
import {TrainerReportService} from "@/components/Reports/trainer-report/trainer-report-service";

export default {
  name: "VueChart",
  components: {
    ICountUp,
  },
  data() {
    return {
      traineeInfo: {
        total: {hraTotal: 0, newTotal: 0},
        notCompleted: {hraNotCompleted: 0, newNotCompleted: 0},
        passed: {hraPass: 0, newPass: 0},
        failed: {hraFail: 0, newFail: 0},
      },

      cpdProviderResponse: [],
      loaded: false,
      loading: false,
      headers: [],
      accreditorAggResp: [],
      cpdProviderAggResp: [],
      traineeOption: null,
      traineeSeries: [],
      prices: [],
      dates: [],
      traineeCountResp: [],
      count: 0,
      delay: 500,
      options: {
        useEasing: true,
        useGrouping: true,
        separator: ",",
        decimal: ".",
        prefix: "",
        suffix: "",
      },
      traineeSummary: {
        TotalCEUTookByTrainee: 0,
        nonAccreditedCourse: 0,
        totalCEUPass: 0,
        totalCEUFail: 0,
        totalEmployeeHRA: 0,
        totalNewEmp: 0,
        traineeTookCEU: 0,
      },
      accreditorSummary: {},
      upcomingRenewals: {
        accreditor: 0,
        cpdProvider: 0,
        newlyAccreditor: 0,
        newlyCpdProvider: 0,
      },
      cpdActivityOptions: null,
      cpdActivitySeries: [],
      courseSummary: null,
      passFailOptions: null,
      totalCoursesOptions: null,
      passFailSeries: [],
      empNumberOptions: null,
      totalTraineesWithCeuSeries: [],
      totalTraineesOption: {},
      totalCoursesTakenSeries: [],
      empNumberSeries: [],
      showNoDashboardMsg: false,
      showHelpFaq: false,
      helpFaqSearch: "",
      helpFaqCategory: "All",
      helpFaqs: [
        {
          category: "Account Access",
          question: "How do I create an iHRIS account?",
          answer:
            "iHRIS accounts are created by the Ministry of Health (MoH) upon receipt of an official request letter. The letter must include the full name of the individual who will use the system, the official name of the institution, and the institution's official email address. Accounts are provided only to CPD providers with an active license.",
        },
        {
          category: "Account Access",
          question: "How do I log in to the iHRIS account?",
          steps: [
            "Connect to the internet.",
            "Open https://hrd.moh.gov.et/ in any browser.",
            "Enter your username and password exactly as provided.",
          ],
        },
        {
          category: "Account Access",
          question: "How can I reset a forgotten username or password?",
          steps: [
            "Go to https://hrd.moh.gov.et/ and click \"Forgot password?\"",
            "Enter the email address registered in the system.",
            "Check your email and follow the instructions to reset your account.",
          ],
        },
        {
          category: "Account Access",
          question: "How do I change my password?",
          steps: [
            "Go to https://hrd.moh.gov.et/ and click \"Forgot password?\"",
            "Enter the email address registered in the system.",
            "Check your email and follow the instructions to reset your account.",
          ],
        },
        {
          category: "CPD Activities",
          question: "What is a CPD activity?",
          answer:
            "CPD activity refers to any CPD-related task like face-to-face training, workshops, seminars, online learning, and others, including CPD Category Two.",
        },
        {
          category: "CPD Activities",
          question: "What is the difference between CPD Category One and Category Two?",
          answer:
            "CPD Category I refers to formal training, online, and eLearning. Category II refers to activities like research, seminars, scientific conferences, grand rounds, workshops, and others listed in the CPD directives.",
        },
        {
          category: "CPD Activities",
          question:
            "How can I collaborate with partners, Regional Health Bureaus, FBOs, and MoH for training activities?",
          answer:
            "The stakeholder's name, stamp, and logo cannot appear in this software; however, the stakeholder's name can appear on the certificate. Enter the stakeholder's name accurately when setting the CPD activity so it appears on the certificate without omissions or errors.",
        },
        {
          category: "CPD Activities",
          question: "Can I register and certify trainees for past trainings?",
          answer:
            "Yes, for a limited number of days, with a clear justification. This also requires permission from the system administrator.",
        },
        {
          category: "Certificates",
          question: "Why is creating a CPD activity and certificate generating difficult?",
          answer:
            "Creating a CPD activity for a CPD category might not be possible if the course is not approved. A certificate cannot be generated when the training duration is incomplete or if the trainee fails to meet the pass mark.",
        },
        {
          category: "Certificates",
          question: "Why might certificate generation be delayed?",
          answer:
            "A certificate cannot be generated when the training duration is incomplete or if the trainee fails to meet the pass mark.",
        },
      ],

      REGION: [],
      ACCREDTOR: [],
      CPD_PROVIDERS: [],
      TRAINER_TYPES: [],
      TRAINER_SELECTION_CRITERIAS: [],
      TRAINER_PROFESSIONS: [],

      regionValue: null,
      startDateValue: null,
      endDateValue: null,
      accreditorValue: null,
      cpdProviderValue: null,
      trainerSelectionCriteriaValue: null,
      trainerTypeValue: null,
      trainerProfessionValue: null,
      accFilter: false,
      cpdFilter: false,
      lastAccreditorParams: null,
      lastCpdParams: null,
      trainerLookupsLoaded: false,

      regionLoading: false,
      accreditorLoading: false,
      cpdLoading: false,
      trainerCriteriaLoading: false,
      trainerTypesLoading: false,
      trainerProfessionLoading: false,
      clearable: true,
      startMenu: false,
      startMenu2: false,

      isCpdProviderUser: false,
      isAccreditorUser: false,
      isRhbUser: false,
      isMohUser: false,

      totalCpd: 0,
      totalAccreditor: 0,
      totalActivity: 0,
      trainerCount: 0,
      trainerCountLoading: false,
      dashboardRefreshTimer: null,
      dashboardRequestId: 0,
      trainerCountRefreshTimer: null,
      chartPalette: ["#6b7cff", "#7ac4fd", "#7dd3a6", "#f5b971", "#c4b5fd"],
      axisLabelColor: "#6b7280",
      accreditorOptions: {
        toolbar: {
          show: true,
        },
        xaxis: {
          categories: [],
        },
      },
      accreditorSeries: [
        {
          name: "Public",
          data: [],
        },
        {
          name: "Professional Associations",
          data: [],
        },
        {
          name: "Others",
          data: [],
        },
      ],
      cpdProviderOptions: {
        xaxis: {
          categories: [],
        },
        toolbar: {
          show: true,
        },
      },
      cpdProviderSeries: [
        {
          name: "Public",
          data: [],
        },
        {
          name: "Private",
          data: [],
        },
        {
          name: "Associations",
          data: [],
        },
        {
          name: "Others",
          data: [],
        },
      ],
    };
  },
  computed: {
    currentDate() {
      return moment().format('dddd, MMMM D, YYYY');
    },
    helpFaqCategories() {
      const categories = this.helpFaqs
          .map((faq) => faq.category)
          .filter((category) => !!category);
      return ["All", ...new Set(categories)];
    },
    filteredHelpFaqs() {
      const selectedCategory = this.helpFaqCategory;
      const query = (this.helpFaqSearch || "").trim().toLowerCase();
      return this.helpFaqs.filter((faq) => {
        const matchesCategory = selectedCategory === "All" || faq.category === selectedCategory;
        if (!matchesCategory) {
          return false;
        }
        if (!query) {
          return true;
        }
        const answerText = faq.answer || "";
        const stepsText = Array.isArray(faq.steps) ? faq.steps.join(" ") : "";
        const haystack = `${faq.question} ${answerText} ${stepsText} ${faq.category}`.toLowerCase();
        return haystack.includes(query);
      });
    },
    readOnlyNotice() {
      const user = getUser();
      if (!user?.data?.readOnly) return null;
      const renewalDate = user?.data?.readOnlyRenewalDate;
      return {
        date: renewalDate ? moment(renewalDate).format("MMMM D, YYYY") : null,
      };
    },
  },

  beforeMount() {
    const userRole = getUser().data.userRoles.roleName;
    this.isCpdProviderUser = userRole == 'CPD Provider ';
    this.isAccreditorUser = userRole == 'Accredit course' || userRole == 'CPD Accreditors ';
    this.isRhbUser = userRole == 'RHB ';
    this.isMohUser = userRole == 'System admins';

    if (this.isMohUser || this.isRhbUser) {
      this.loadRegions();
    }
  },
  async created() {
    this.loading = true;
    const user = getUser();
    if (user.data.facilityType && user.data.facilityType.code == "EINST") {
      this.showNoDashboardMsg = true;
      this.loading = false;
      return;
    }

    this.initTraineeChart();
    const overviewPromise = this.loadDashboardOverview();
    this.fetchTrainerCount();
    try {
      await overviewPromise;
    } finally {
      this.loading = false;
    }
  },
  beforeDestroy() {
    if (this.dashboardRefreshTimer) {
      clearTimeout(this.dashboardRefreshTimer);
    }
    if (this.trainerCountRefreshTimer) {
      clearTimeout(this.trainerCountRefreshTimer);
    }
  },
  watch: {
    regionValue: function (val) {
      this.regionValue = val == '' ? null : val;
      this.fetchAccreditors();
      this.fetchCPDProviders();
      this.scheduleDashboardRefresh();
    },
    accreditorValue: function (val) {
      this.accreditorValue = val == '' ? null : val;
      this.accFilter = this.accreditorValue ? true : false;
      this.fetchCPDProviders();
      this.scheduleDashboardRefresh();
    },
    cpdProviderValue: function (val) {
      this.cpdProviderValue = val == '' ? null : val;
      this.scheduleDashboardRefresh();
    },
    startDateValue: function (val) {
      this.startDateValue = val == '' ? null : val;
      this.scheduleDashboardRefresh();
    },
    endDateValue: function (val) {
      this.endDateValue = val == '' ? null : val;
      this.scheduleDashboardRefresh();
    },
    trainerSelectionCriteriaValue: function (val) {
      this.trainerSelectionCriteriaValue = val == '' ? null : val;
      this.scheduleTrainerCountRefresh();
    },
    trainerTypeValue: function (val) {
      this.trainerTypeValue = val == '' ? null : val;
      this.scheduleTrainerCountRefresh();
    },
    trainerProfessionValue: function (val) {
      this.trainerProfessionValue = val == '' ? null : val;
      this.scheduleTrainerCountRefresh();
    },
  },
  methods: {
    scheduleDashboardRefresh() {
      if (this.dashboardRefreshTimer) {
        clearTimeout(this.dashboardRefreshTimer);
      }
      this.dashboardRefreshTimer = setTimeout(() => {
        this.fetchOnFilters();
      }, 250);
      this.scheduleTrainerCountRefresh();
    },
    scheduleTrainerCountRefresh() {
      if (this.trainerCountRefreshTimer) {
        clearTimeout(this.trainerCountRefreshTimer);
      }
      this.trainerCountRefreshTimer = setTimeout(() => {
        this.fetchTrainerCount();
      }, 250);
    },
    resetHelpFaqFilters() {
      this.helpFaqSearch = "";
      this.helpFaqCategory = "All";
    },
    openHelpFaq() {
      this.resetHelpFaqFilters();
      this.showHelpFaq = true;
    },
    closeHelpFaq() {
      this.showHelpFaq = false;
    },
    async loadRegions() {
      this.regionLoading = true;
      try {
        const regionList = await getRegionList();
        this.REGION = (regionList || []).filter(region => region?.name);
      } finally {
        this.regionLoading = false;
      }
    },
    ensureAccreditorsLoaded() {
      if (!this.ACCREDTOR || this.ACCREDTOR.length === 0) {
        this.fetchAccreditors();
      }
    },
    ensureCPDProvidersLoaded() {
      if (!this.CPD_PROVIDERS || this.CPD_PROVIDERS.length === 0) {
        this.fetchCPDProviders();
      }
    },
    ensureTrainerLookupsLoaded() {
      if (!this.trainerLookupsLoaded) {
        this.loadTrainerLookups();
      }
    },
    buildDashboardQuery() {
      const params = new URLSearchParams();
      if (this.regionValue?.id != null) {
        params.set("regionId", this.regionValue.id);
      }
      if (this.accreditorValue?.id != null) {
        params.set("accreditorId", this.accreditorValue.id);
      }
      if (this.cpdProviderValue?.id != null) {
        params.set("cpdId", this.cpdProviderValue.id);
      }
      if (this.startDateValue) {
        params.set("startDate", this.startDateValue);
      }
      if (this.endDateValue) {
        params.set("endDate", this.endDateValue);
      }

      const queryString = params.toString();
      return queryString ? `?${queryString}` : "";
    },
    applyDashboardOverview(payload = {}) {
      this.traineeInfo = payload?.traineeInfo || {
        total: {hraTotal: 0, newTotal: 0},
        notCompleted: {hraNotCompleted: 0, newNotCompleted: 0},
        passed: {hraPass: 0, newPass: 0},
        failed: {hraFail: 0, newFail: 0},
      };
      this.traineeSummary = payload?.traineeSummary || {
        TotalCEUTookByTrainee: 0,
        nonAccreditedCourse: 0,
      };
      this.accreditorSummary = payload?.accreditorSummary || {};
      this.upcomingRenewals = payload?.upcomingRenewals || {
        accreditor: 0,
        cpdProvider: 0,
        newlyAccreditor: 0,
        newlyCpdProvider: 0,
      };
      this.traineeCountResp = Array.isArray(payload?.traineeCountResp)
          ? payload.traineeCountResp
          : [];
      this.courseSummary = payload?.courseSummary || {category1: 0, category2: 0};
      this.accreditorAggResp = Array.isArray(payload?.accreditorAggResp)
          ? payload.accreditorAggResp
          : [];
      this.cpdProviderAggResp = Array.isArray(payload?.cpdProviderAggResp)
          ? payload.cpdProviderAggResp
          : [];

      this.passFailSeries = [
        Number(this.traineeSummary?.totalCEUPass) || 0,
        Number(this.traineeSummary?.totalCEUFail) || 0,
      ];
      this.empNumberSeries = [
        Number(this.traineeSummary?.totalEmployeeHRA) || 0,
        Number(this.traineeSummary?.totalNewEmp) || 0,
      ];
      this.totalCoursesTakenSeries[0] = Number(this.traineeSummary?.TotalCEUTookByTrainee) || 0;
      this.totalTraineesWithCeuSeries[0] = Number(this.traineeSummary?.traineeTookCEU) || 0;

      this.updateTraineeChart();
      this.UpdateActivityChart();
      this.updateAccreditor();
      this.updateCpd();
      this.loaded = true;
    },
    async loadDashboardOverview(params = "") {
      const requestId = ++this.dashboardRequestId;
      try {
        const overviewResp = await DashboardService.getOverview(params);

        if (requestId !== this.dashboardRequestId) {
          return false;
        }

        this.applyDashboardOverview(overviewResp?.data || {});
        return true;
      } catch (error) {
        if (requestId === this.dashboardRequestId) {
          console.error(error);
          this.$toast.error("Unable to load dashboard data. Please try again.");
        }
        return false;
      }
    },
    initTraineeChart() {
      this.traineeSeries = [
        {
          name: "Total Trainee",
          data: [],
        },
      ];

      this.traineeOption = {
        chart: {
          type: "area",
          height: 360,
          zoom: {
            enabled: false,
          },
          toolbar: {
            show: false,
          },
          dropShadow: {
            enabled: true,
            top: 12,
            blur: 10,
            color: this.chartPalette[0],
            opacity: 0.18,
          },
        },
        colors: [this.chartPalette[0]],
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "smooth",
          width: 3,
        },
        fill: {
          type: "gradient",
          gradient: {
            shadeIntensity: 0.9,
            opacityFrom: 0.5,
            opacityTo: 0.05,
            stops: [0, 55, 100],
          },
        },
        title: {
          text: "Trainees Trend",
          align: "left",
          offsetX: 6,
          style: {
            fontSize: "16px",
            fontWeight: 700,
            color: "#0f172a",
          },
        },
        labels: [],
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 5,
        },
        xaxis: {
          type: "datetime",
          labels: {
            style: {
              colors: [this.axisLabelColor],
            },
          },
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            style: {
              colors: [this.axisLabelColor],
            },
            formatter: (val) => Math.round(val),
          },
          tickAmount: 4,
          decimalsInFloat: 0,
        },
        markers: {
          size: 4,
          strokeWidth: 2,
          strokeColors: "#ffffff",
          hover: {
            size: 7,
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
          labels: {
            colors: [this.axisLabelColor],
          },
        },
        tooltip: {
          theme: "light",
          x: {
            format: "dd MMM yyyy",
          },
          y: {
            formatter: (value) => (value || 0).toLocaleString(),
          },
        },
      };
    },
    async fetchAccreditors() {
      this.accreditorLoading = true;
      const params = this.regionValue ? `?regionId=${this.regionValue.id}` : '';
      if (this.lastAccreditorParams === params && this.ACCREDTOR.length > 0) {
        this.accreditorLoading = false;
        return;
      }
      const accreditorList = await AccreditorService.getAccreditorsDrp(params);
      this.ACCREDTOR = accreditorList?.data || [];
      this.lastAccreditorParams = params;
      this.accreditorLoading = false;
    },
    async fetchCPDProviders() {
      this.cpdLoading = true;
      const queryParts = [];
      this.regionValue !== null && queryParts.push(`regionId=${this.regionValue.id}`);
      this.accreditorValue !== null && queryParts.push(`accreditorId=${this.accreditorValue.id}`);
      const params = queryParts.length ? `?${queryParts.join("&")}` : '';
      if (this.lastCpdParams === params && this.CPD_PROVIDERS.length > 0) {
        this.cpdLoading = false;
        return;
      }
      const cpdProviderList = await TrainingCenterService.getTrainingCentersDrps(params);
      this.CPD_PROVIDERS = cpdProviderList?.data || [];
      this.lastCpdParams = params;
      this.cpdLoading = false;
    },
    async loadTrainerLookups() {
      if (this.trainerLookupsLoaded) return;
      this.trainerCriteriaLoading = true;
      this.trainerTypesLoading = true;
      this.trainerProfessionLoading = true;
      try {
        const [trainerTypes, trainerCriteria, healthProfs] = await Promise.all([
          getTrainerTypes(),
          getTrainerSelectionCriteriaList(),
          getHealthProfessions(),
        ]);
        this.TRAINER_TYPES = Array.isArray(trainerTypes) ? trainerTypes : [];

        const criteriaRows = trainerCriteria?.rows || [];
        this.TRAINER_SELECTION_CRITERIAS = criteriaRows.filter((data) => data?.isActive ?? true);

        const professionRows = healthProfs?.rows || [];
        this.TRAINER_PROFESSIONS = professionRows.filter((data) => data?.isActive ?? true);
        this.trainerLookupsLoaded = true;
      } finally {
        this.trainerCriteriaLoading = false;
        this.trainerTypesLoading = false;
        this.trainerProfessionLoading = false;
      }
    },
    async fetchTrainerCount() {
      this.trainerCountLoading = true;
      const params = new URLSearchParams(this.buildDashboardQuery());
      if (this.trainerSelectionCriteriaValue?.id != null) {
        params.set("trainerSelectionCriteriaId", this.trainerSelectionCriteriaValue.id);
      }
      if (this.trainerTypeValue?.id != null) {
        params.set("trainerTypeId", this.trainerTypeValue.id);
      }
      if (this.trainerProfessionValue?.id != null) {
        params.set("professionId", this.trainerProfessionValue.id);
      }
      const searchParams = params.toString() ? `?${params.toString()}` : "";

      try {
        const res = await TrainerReportService.getTrainerCount(searchParams);
        if (res?.status === 200) {
          const data = res?.data?.data;
          this.trainerCount = Number(data?.count) || 0;
        } else {
          this.$toast.error("There was a problem fetching trainer summary, Please try Again!");
        }
      } catch (error) {
        console.error(error);
        this.$toast.error("There was a problem fetching trainer summary, Please try Again!");
      } finally {
        this.trainerCountLoading = false;
      }
    },
    async fetchOnFilters() {
      await this.loadDashboardOverview(this.buildDashboardQuery());
    },

    async updateTraineeChart(){
      if (!this.traineeOption || !this.traineeOption.labels) {
        this.initTraineeChart();
      }
      this.traineeSeries = [
        {
          name: "Total Trainee",
          data: [],
        },
      ];
      this.traineeOption.labels = [];
      this.traineeCountResp.forEach((data) => {
        this.traineeOption.labels.push(
            moment(data.courseCompletionDate).format("DD MMM YYYY")
        );
        this.traineeSeries[0].data.push(+data.traineeCount);
      });
    },
    async UpdateActivityChart() {
      if (!this.courseSummary) {
        return;
      }
      const activityTotals = [
        Number(this.courseSummary.category1) || 0,
        Number(this.courseSummary.category2) || 0,
      ];
      this.totalActivity = activityTotals.reduce((sum, value) => sum + value, 0);
      this.cpdActivityOptions = {
        chart: {
          type: "donut",
          toolbar: {
            show: false,
          },
        },
        colors: [this.chartPalette[2], this.chartPalette[3], this.chartPalette[4]],
        stroke: {
          width: 0,
        },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Math.round(val)}%`,
          style: {
            fontSize: "13px",
            fontWeight: 600,
          },
        },
        plotOptions: {
          pie: {
            expandOnClick: true,
            donut: {
              size: "72%",
              labels: {
                show: true,
                name: {
                  fontSize: "13px",
                  offsetY: 6,
                  color: this.axisLabelColor,
                },
                value: {
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#111827",
                  formatter: (val) => Math.round(val),
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: "Total",
                  color: this.axisLabelColor,
                  formatter: (w) =>
                      w.globals.seriesTotals.reduce((a, b) => a + b, 0),
                },
              },
            },
          },
        },
        legend: {
          position: "bottom",
          horizontalAlign: "center",
          fontSize: "13px",
          labels: {
            colors: this.axisLabelColor,
          },
          itemMargin: {
            horizontal: 10,
            vertical: 6,
          },
        },
        tooltip: {
          y: {
            formatter: (value) => (value || 0).toLocaleString(),
          },
        },
        responsive: [
          {
            breakpoint: 600,
            options: {
              legend: {
                position: "bottom",
              },
            },
          },
        ],
        labels: ["Category 1", "Category 2"],
      };
      this.cpdActivitySeries = activityTotals;
    },
    async updateAccreditor() {
      this.totalAccreditor = 0;
      this.accreditorAggResp.forEach(item => {
        const publicCount = Number(item.public) || 0;
        const privateCount = Number(item.private) || 0;
        const professionalCount = Number(item.professionalAssociations) || 0;
        const othersCount = Number(item.others) || 0;
        this.totalAccreditor += publicCount + privateCount + professionalCount + othersCount;
      });
      this.accreditorOptions = {
        chart: {
          type: "bar",
          toolbar: {
            show: false,
          },
          fontFamily: 'inherit',
        },
        colors: ['#8b5cf6', '#22c55e', '#f97316'],
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "60%",
            dataLabels: {
              position: "top",
            },
            distributed: false,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: "11px",
            fontWeight: 600,
            colors: ["#475569"],
          },
          formatter: (val) => val || '',
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        grid: {
          borderColor: "#f1f5f9",
          strokeDashArray: 0,
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        xaxis: {
          categories: [],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            rotate: 0,
            style: {
              colors: this.axisLabelColor,
              fontSize: '10px',
              fontWeight: 500,
            },
            trim: true,
            maxHeight: 60,
          },
        },
        yaxis: {
          min: 0,
          tickAmount: 4,
          labels: {
            style: {
              colors: [this.axisLabelColor],
              fontSize: '10px',
            },
            formatter: (val) => Math.round(val),
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
          fontSize: '10px',
          fontWeight: 500,
          labels: {
            colors: this.axisLabelColor,
          },
          markers: {
            width: 8,
            height: 8,
            radius: 4,
          },
          itemMargin: {
            horizontal: 8,
            vertical: 4,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: 'light',
          y: {
            formatter: (value) => (value || 0).toLocaleString(),
          },
        },
      };
      this.accreditorSeries = [
        {
          name: "Public",
          data: [],
        },
        {
          name: "Professional Associations",
          data: [],
        },
        {
          name: "Others",
          data: [],
        },
      ];
      this.accreditorAggResp.forEach((data) => {
        this.accreditorOptions.xaxis.categories.push(data.accreditorOwnership);
        this.accreditorSeries[0].data.push(Number(data.public) || 0);
        this.accreditorSeries[1].data.push(Number(data.professionalAssociations) || 0);
        this.accreditorSeries[2].data.push(Number(data.others) || 0);
      });
    },
    async updateCpd() {
      this.totalCpd = 0;
      this.cpdProviderAggResp.forEach(item => {
        const publicCount = Number(item.public) || 0;
        const privateCount = Number(item.private) || 0;
        const associationCount = Number(item.associations) || 0;
        const othersCount = Number(item.others) || 0;
        this.totalCpd += publicCount + privateCount + associationCount + othersCount;
      });

      this.cpdProviderOptions = {
        chart: {
          type: "bar",
          toolbar: {
            show: false,
          },
          fontFamily: 'inherit',
        },
        colors: ['#3b82f6', '#06b6d4', '#22c55e', '#f97316'],
        plotOptions: {
          bar: {
            borderRadius: 6,
            columnWidth: "65%",
            dataLabels: {
              position: "top",
            },
            distributed: false,
          },
        },
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: {
            fontSize: "11px",
            fontWeight: 600,
            colors: ["#475569"],
          },
          formatter: (val) => val || '',
        },
        stroke: {
          show: true,
          width: 2,
          colors: ['transparent']
        },
        grid: {
          borderColor: "#f1f5f9",
          strokeDashArray: 0,
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        xaxis: {
          categories: [],
          axisBorder: {
            show: false,
          },
          axisTicks: {
            show: false,
          },
          labels: {
            rotate: 0,
            style: {
              colors: this.axisLabelColor,
              fontSize: '10px',
              fontWeight: 500,
            },
            trim: true,
            maxHeight: 60,
          },
        },
        yaxis: {
          min: 0,
          tickAmount: 4,
          labels: {
            style: {
              colors: [this.axisLabelColor],
              fontSize: '10px',
            },
            formatter: (val) => Math.round(val),
          },
        },
        legend: {
          position: "top",
          horizontalAlign: "center",
          fontSize: '10px',
          fontWeight: 500,
          labels: {
            colors: this.axisLabelColor,
          },
          markers: {
            width: 8,
            height: 8,
            radius: 4,
          },
          itemMargin: {
            horizontal: 8,
            vertical: 4,
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          theme: 'light',
          y: {
            formatter: (value) => (value || 0).toLocaleString(),
          },
        },
      };
      this.cpdProviderSeries = [
        {
          name: "Public",
          data: [],
        },
        {
          name: "Private",
          data: [],
        },
        {
          name: "Associations",
          data: [],
        },
        {
          name: "Others",
          data: [],
        },
      ];
      this.cpdProviderAggResp.forEach((data) => {
        this.cpdProviderOptions.xaxis.categories.push(data.cpdProviderOwnership);
        this.cpdProviderSeries[0].data.push(Number(data.public) || 0);
        this.cpdProviderSeries[1].data.push(Number(data.private) || 0);
        this.cpdProviderSeries[2].data.push(Number(data.associations) || 0);
        this.cpdProviderSeries[3].data.push(Number(data.others) || 0);
      });
    },

  },
};
</script>

<style scoped>
/* ===== Loading State ===== */
.loading-container {
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.loading-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
}

.loading-logo {
  position: relative;
  margin-bottom: 24px;
}

.loading-icon-wrapper {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  animation: iconBounce 2s ease-in-out infinite;
}

.loading-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  background: rgba(59, 130, 246, 0.2);
  border-radius: 18px;
  animation: pulse 2s ease-in-out infinite;
}

.loading-text {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 6px;
}

.loading-subtext {
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 24px;
}

.loading-progress {
  width: 200px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 20px;
}

.loading-progress-bar {
  height: 100%;
  width: 40%;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  border-radius: 4px;
  animation: progressSlide 1.5s ease-in-out infinite;
}

.loading-dots {
  display: flex;
  gap: 8px;
}

.loading-dots .dot {
  width: 8px;
  height: 8px;
  background: #cbd5e1;
  border-radius: 50%;
  animation: dotPulse 1.4s ease-in-out infinite;
}

.loading-dots .dot:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots .dot:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots .dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes iconBounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.3);
    opacity: 0;
  }
}

@keyframes progressSlide {
  0% {
    transform: translateX(-100%);
  }
  50% {
    transform: translateX(150%);
  }
  100% {
    transform: translateX(400%);
  }
}

@keyframes dotPulse {
  0%, 80%, 100% {
    transform: scale(0.8);
    background: #cbd5e1;
  }
  40% {
    transform: scale(1.2);
    background: #3b82f6;
  }
}

/* ===== Dashboard Wrapper ===== */
.dashboard-wrapper {
  min-height: 100vh;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.dashboard-content {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

/* ===== Hero Section ===== */
.dashboard-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
  margin-bottom: 24px;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-icon {
  width: 52px;
  height: 52px;
  background: linear-gradient(135deg, #dbeafe, #bfdbfe);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-text {
  display: flex;
  flex-direction: column;
}

.dashboard-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
}

.dashboard-subtitle {
  margin: 4px 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.hero-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.hero-date {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: #f8fafc;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 500;
  color: #64748b;
  border: 1px solid #e2e8f0;
}

.hero-help-btn {
  background: linear-gradient(135deg, #eff6ff, #dbeafe) !important;
  border: 1px solid #bfdbfe !important;
  color: #1d4ed8 !important;
  text-transform: none;
  font-weight: 700;
  letter-spacing: normal;
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.12);
}

.hero-help-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.18);
}

.dashboard-help-dialog {
  border-radius: 18px !important;
  overflow: hidden;
}

.dashboard-help-dialog__title {
  align-items: center;
  background: linear-gradient(135deg, #f8fafc, #eff6ff);
  border-bottom: 1px solid #e2e8f0;
}

.dashboard-help-dialog__title-wrap {
  display: grid;
  gap: 2px;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.dashboard-help-dialog__eyebrow {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.dashboard-help-dialog__body {
  color: #1e293b;
  padding-top: 18px;
}

.dashboard-help-dialog__intro {
  font-size: 0.9rem;
  color: #475569;
}

.dashboard-help-toolbar {
  display: grid;
  gap: 12px;
}

.dashboard-help-search {
  max-width: 460px;
}

.dashboard-help-categories {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.dashboard-help-chip {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #334155;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.dashboard-help-chip:hover {
  border-color: #93c5fd;
  color: #1d4ed8;
}

.dashboard-help-chip--active {
  background: #1d4ed8;
  border-color: #1d4ed8;
  color: #ffffff;
}

.dashboard-help-results-count {
  margin: 12px 0;
  font-size: 0.82rem;
  color: #64748b;
}

.dashboard-help-panel__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-right: 10px;
}

.dashboard-help-panel__index {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #334155;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
}

.dashboard-help-panel__question {
  font-weight: 600;
  color: #0f172a;
  line-height: 1.35;
}

.dashboard-help-panel__category {
  margin-left: auto;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #2563eb;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 999px;
  padding: 2px 8px;
}

.dashboard-help-dialog :deep(.v-expansion-panel) {
  border-radius: 12px !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.dashboard-help-dialog :deep(.v-expansion-panel::before) {
  box-shadow: none !important;
}

.dashboard-help-dialog :deep(.v-expansion-panel-header) {
  min-height: 64px;
}

.dashboard-help-dialog :deep(.v-expansion-panel-content__wrap) {
  padding-top: 2px;
  padding-bottom: 16px;
}

.dashboard-help-dialog__answer {
  margin: 0 0 8px;
  line-height: 1.5;
}

.dashboard-help-dialog__steps {
  margin: 0;
  padding-left: 18px;
}

.dashboard-help-dialog__steps li {
  margin-bottom: 6px;
}

.dashboard-help-empty {
  margin-top: 6px;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 24px 18px;
  text-align: center;
  color: #64748b;
}

.dashboard-help-empty__title {
  margin-top: 8px;
  color: #1e293b;
  font-weight: 700;
}

.dashboard-help-empty__subtitle {
  margin-top: 4px;
  font-size: 0.84rem;
}

.dashboard-help-footer {
  margin-top: 18px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
  font-size: 0.82rem;
  color: #64748b;
}

/* ===== Renewal Alert ===== */
.renewal-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 18px;
  margin-bottom: 24px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(124, 45, 18, 0.06);
}

.renewal-alert__icon {
  color: #f97316;
  margin-top: 2px;
}

.renewal-alert__title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #9a3412;
}

.renewal-alert__text {
  margin-top: 4px;
  font-size: 0.85rem;
  color: #9a3412;
  line-height: 1.4;
}

/* ===== Filter Section ===== */
.filter-section {
  background: #ffffff;
  border-radius: 14px;
  padding: 20px 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.filter-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f1f5f9;
}

.filter-icon {
  color: #64748b;
}

.filter-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.filter-item {
  min-width: 0;
}

.modern-input :deep(.v-input__slot) {
  border-radius: 10px !important;
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
  transition: all 0.2s ease;
}

.modern-input :deep(.v-input__slot:hover) {
  border-color: #cbd5e1 !important;
}

.modern-input :deep(.v-input--is-focused .v-input__slot) {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
}

/* ===== Section Headers ===== */
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.section-icon {
  color: #3b82f6;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

/* ===== KPI Section ===== */
.kpi-section {
  margin-bottom: 28px;
}

.kpi-grid {
  margin: 0 -8px;
}

.kpi-col {
  padding: 8px;
}

@media (min-width: 960px) {
  .kpi-col {
    flex: 0 0 20%;
    max-width: 20%;
  }
}

.kpi-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.04) !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 90px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
  border-color: rgba(100, 116, 139, 0.15) !important;
}

/* ===== Trainer Section ===== */
.trainer-section {
  margin-bottom: 28px;
}

.trainer-grid {
  display: grid;
  grid-template-columns: minmax(0, 2.2fr) minmax(0, 1fr);
  gap: 16px;
}

.trainer-panel {
  background: #ffffff;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.trainer-panel__header {
  margin-bottom: 16px;
}

.trainer-panel__title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e293b;
}

.trainer-panel__subtitle {
  margin-top: 4px;
  font-size: 0.8rem;
  color: #64748b;
}

.trainer-panel--summary {
  display: flex;
  align-items: center;
}

.trainer-summary {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.trainer-summary__label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #64748b;
  font-weight: 600;
}

.trainer-summary__value {
  font-size: 2.2rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 10px;
}

.trainer-summary__meta {
  font-size: 0.8rem;
  color: #64748b;
}

.trainer-summary__details {
  margin-top: 8px;
  display: grid;
  gap: 8px;
}

.trainer-summary__item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.85rem;
  color: #1e293b;
}

.trainer-summary__item-label {
  color: #64748b;
  font-weight: 500;
}

.trainer-summary__item-value {
  font-weight: 600;
}

@media (max-width: 960px) {
  .trainer-grid {
    grid-template-columns: 1fr;
  }
}

.kpi-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  flex: 1;
}

.kpi-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.kpi-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.kpi-icon :deep(.v-icon) {
  font-size: 20px !important;
  color: #64748b !important;
}

.kpi-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  line-height: 1.3;
}

.kpi-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #1e293b;
  margin-top: auto;
}

.kpi-value .iCountUp {
  font-size: inherit;
  color: inherit;
}

/* ===== Charts Section ===== */
.charts-section {
  margin-top: 8px;
}

.charts-grid {
  margin: 16px -8px 0;
}

.charts-grid > .col {
  padding: 8px;
  display: flex;
}

.charts-grid > .col > .chart-card {
  width: 100%;
}

.chart-card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  transition: all 0.25s ease;
}

.chart-card:hover {
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
}

.chart-card--full {
  margin-bottom: 16px;
}

.chart-card--fixed {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.chart-card--fixed .chart-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-body--centered {
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-header {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chart-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.chart-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}

.chart-title {
  font-weight: 600;
  font-size: 0.875rem;
  color: #1e293b;
}

.chart-subtitle {
  font-size: 0.7rem;
  color: #64748b;
  margin-top: 1px;
}

.chart-body {
  padding: 12px 14px 8px;
}

/* ===== No Data State ===== */
.no-data-container {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
}

.no-data-content {
  text-align: center;
}

.no-data-icon {
  margin-bottom: 20px;
  opacity: 0.6;
}

.no-data-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.no-data-subtitle {
  font-size: 1rem;
  color: #64748b;
}

/* ===== CountUp Override ===== */
.iCountUp {
  font-size: inherit;
  margin: 0;
  color: inherit;
}

/* ===== Responsive ===== */
@media (max-width: 768px) {
  .dashboard-content {
    padding: 12px;
  }

  .dashboard-hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: 20px;
  }

  .hero-right {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .hero-date {
    width: 100%;
    justify-content: center;
  }

  .hero-help-btn {
    width: 100%;
  }

  .dashboard-help-panel__category {
    display: none;
  }
  .filter-grid {
    grid-template-columns: 1fr;
  }

  .kpi-col {
    flex: 0 0 50% !important;
    max-width: 50% !important;
  }
}

@media (max-width: 480px) {
  .kpi-col {
    flex: 0 0 100% !important;
    max-width: 100% !important;
  }
}
</style>
